import { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { Layers, Route, BarChart3 } from "lucide-react";
import "./solution.css";

import platformPanels from "../../../assets/platform-panels.webp";
import guidedSchedule from "../../../assets/tasks.webp";
import careerCenterData from "../../../assets/career-center-data.webp";

const capabilities = [
  {
    tone: "blue",
    panels: true,
    eyebrow: "One Platform",
    icon: Layers,
    title: "Every step in one place",
    description:
      "Job postings, career exploration, resumes, networking, interview prep, and application tracking all live in one platform, not scattered across different sites that never talk to each other.",
    image: platformPanels,
    ratio: "1659 / 804",
    alt: "RAIN panels side by side: career exploration, application tracker, resume review, the RAIN assistant, interview prep, networking, and tasks",
  },
  {
    tone: "violet",
    eyebrow: "Guidance",
    icon: Route,
    title: "A clear next step, every single day",
    description:
      "Because it all sits in one place, RAIN can read across everything and turn it into an ordered plan, so students always know the next move instead of guessing at it.",
    image: guidedSchedule,
    ratio: "1549 / 1038",
    alt: "A RAIN generated schedule listing each task, when to do it, and a one-click action",
  },
  {
    tone: "teal",
    eyebrow: "Visibility",
    icon: BarChart3,
    title: "See who needs help, and why",
    description:
      "Career centers get engagement and outcomes across the whole student journey, not fragments from a single tool, so advisors know where to spend their time.",
    image: careerCenterData,
    ratio: "5158 / 2657",
    alt: "RAIN career center portal: headline counts, an individual student's progress, resume analytics, networking activity, interview prep, and outcomes by major",
  },
];

/* Scroll-stacking deck.
   Each card parks just under the navbar, the next one scrolls up over it,
   and every covered card recedes a little so the stack reads as depth. The
   park offsets themselves live in solution.css (--stack-top, --stack-step,
   the latter leaving a sliver of each parked card visible above the one
   covering it) so the narrow breakpoint can tighten them; only the index
   travels from here.

   A card pulls back 10% as soon as one card covers it, then keeps easing
   back a little further for each card stacked after that. */
const SHRINK_FIRST = 0.1;
const SHRINK_EXTRA = 0.04;

const CapabilityCard = ({ card, index, total, progress }) => {
  const { tone, panels, eyebrow, icon: Icon, title, description, image, ratio, alt } =
    card;

  // How many cards will end up stacked on top of this one. The last card
  // is never covered, so it stays at full size.
  const covered = total - 1 - index;
  const targetScale =
    covered === 0 ? 1 : 1 - (SHRINK_FIRST + (covered - 1) * SHRINK_EXTRA);
  const scale = useTransform(progress, [index / total, 1], [1, targetScale]);

  return (
    <motion.article
      className={`home-cap-card home-cap-${tone}${
        panels ? " home-cap-panels" : ""
      }`}
      /* The park offset is handed to CSS rather than set here, because a
         narrow screen wants a shallower one: the navbar is 56px there against
         64px on the desktop, and every pixel spent parking a card is a pixel
         that card has to give back before it fits the viewport. */
      style={{ "--stack-index": index, scale }}
    >
      <div className="home-cap-copy">
        <p className="home-eyebrow home-cap-eyebrow">
          <Icon size={14} strokeWidth={1.75} />
          {eyebrow}
        </p>
        <h3 className="home-cap-title">{title}</h3>
        <p className="home-cap-desc">{description}</p>
      </div>

      <div className="home-cap-visual" style={{ "--visual-ratio": ratio }}>
        <img decoding="async" src={image} alt={alt} loading="lazy" />
      </div>
    </motion.article>
  );
};

const Solution = () => {
  const stackRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: stackRef,
    offset: ["start start", "end end"],
  });

  return (
    <section className="home-solution" id="solution">
      <div className="home-container">
        <div className="home-solution-head">
          <p className="home-eyebrow">The Solution</p>

          {/* One continuous sentence. It used to be three forced lines, which
              read as a tidy three-part statement at full width and fell apart
              below it: each line wrapped on its own and centred on its own, so
              the whole thing came out ragged. Now it flows and balances, and
              the commas do the separating the line breaks used to do. The
              three verb phrases carry the weight on their own — the icons that
              used to annotate them are gone. */}
          <h2 className="home-solution-statement">
            <span className="sol-muted">One platform that</span>{" "}
            <span className="sol-strong">unites every step,</span>{" "}
            <span className="sol-strong">guides every student,</span>{" "}
            <span className="sol-muted">and</span>{" "}
            <span className="sol-strong">
              gives career centers the full picture
            </span>
          </h2>
        </div>

        <div className="home-solution-cards" ref={stackRef}>
          {capabilities.map((card, i) => (
            <CapabilityCard
              key={card.eyebrow}
              card={card}
              index={i}
              total={capabilities.length}
              progress={scrollYProgress}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solution;
